import {createSelector} from 'reselect'
import omit from 'lodash/omit.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  useSelector,
  onlyIfForm,
} from '../../../store.js'
import {
  PluralBlock,
  Count,
  plural,
  IfPlural,
  IfSingle,
  Plural,
} from '../../../common/components/Plural.js'
import apiverson from '../../../common/apiverson.js'
import {isPresent} from '../../../common/utils.js'
import {PRODUCT_LIST_PAGE} from '../../../common/constants/Pages.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import StocktakeFilter from '../../../common/components/StocktakeFilter.js'
import {currentPageSelector} from '../../../redux/selectors/ui/index.js'
import {refreshProductList} from '../../ProductListPage/productListFunctions.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import ButtonLink from '../../../common/components/Button/ButtonLink.js'
import {buildPath, stringIt} from '../../../common/querystring.js'
import {setStocktake} from '../../../data/stocktakes.js'
import {isKitSelector} from '../../../data/products.js'
import {API_PRODUCT_KIT} from '../../../common/constants/Products.js'
import api from '../../../common/api.js'
import className from '../../../common/className.js'
import Icon from '../../../common/icons/Icon.js'
import Checkbox from '../../../common/components/Checkbox.js'

const MODAL_FORM = 'add_to_stocktake_modal'

export async function showAddToStocktakeModal({
  skus = [],
  referenceID = null,
  productListQueryParams = null,
  productListCount = 0,
}) {
  setForm(MODAL_FORM, {
    referenceID,
    skus,
    productListQueryParams:
      skus.length === 0 && productListQueryParams
        ? omit(productListQueryParams, ['limit', 'offset'])
        : null,
    productListCount,
    countOfKitSKUs: skus
      .map((sku) => isKitSelector(getState(), {sku}))
      .filter((v) => v).length,
    hasNoKitFilter:
      skus.length === 0 && productListQueryParams
        ? productListQueryParams[API_PRODUCT_KIT] === false
        : false,
    hasKitFilter:
      skus.length === 0 && productListQueryParams
        ? productListQueryParams[API_PRODUCT_KIT]
        : false,
    autoStart: false,
    isSaving: false,
    serverError: null,
  })

  await checkForKitsInQuery()
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

export const errorsSelector = createSelector(
  modalFormSelector,
  ({referenceID, skus, productListCount}) => {
    const errors = {}

    if (!isPresent(referenceID)) {
      errors.referenceID = 'Session ID is required'
      errors.preventSave = true
    }

    if (skus.length + productListCount === 0) {
      errors.preventSave = true
    }

    return errors
  },
)

export async function checkForKitsInQuery() {
  const {productListQueryParams, hasNoKitFilter} = modalFormSelector(getState())

  if (!productListQueryParams || hasNoKitFilter) {
    return
  }

  try {
    updateModalForm({isSaving: true, serverError: null})

    const {
      json: {count: countOfKitSKUs},
    } = await api.get('/product', {
      ...productListQueryParams,
      [API_PRODUCT_KIT]: true,
      limit: 0,
    })

    updateModalForm({countOfKitSKUs})
  } catch (err) {
    updateModalForm({
      serverError: `Error loading Add to Stocktake modal: ${
        err.message || err.error_message
      }`,
    })
  } finally {
    updateModalForm({isSaving: false})
  }
}

export async function addToStocktake() {
  try {
    const {referenceID, skus, productListQueryParams, autoStart} =
      modalFormSelector(getState())

    updateModalForm({isSaving: true, serverError: null})
    const criteria = productListQueryParams
      ? stringIt(productListQueryParams)
      : {sku: skus}

    const {json} = await apiverson.post(
      buildPath(['stocktake', referenceID, 'line']),
      {
        criteria,
      },
    )

    setStocktake(json)

    if (autoStart) {
      await apiverson.post(buildPath(['stocktake', referenceID, 'start']), {
        criteria,
      })
    }

    showMessageToast(plural(skus)`Added product${['s']} to stocktake.`)

    closeModal()

    if (currentPageSelector(getState()) === PRODUCT_LIST_PAGE) {
      await refreshProductList()
    }
  } catch (err) {
    updateModalForm({
      serverError: `Error adding products to stocktake: ${
        err.message || err.error_message
      }`,
      isSaving: false,
    })
  }
}

function AddToStocktakeModal({form}) {
  const errors = useSelector(errorsSelector)
  const totalToBeAdded = form.skus.length + form.productListCount
  const canAddToStocktake = totalToBeAdded > 0
  const showKitNotice = !form.hasNoKitFilter && form.countOfKitSKUs > 0

  return (
    <ConfirmModal
      title={plural(
        totalToBeAdded,
      )`Add Product${['s']} to an Existing Stocktake`}
      modalSize="sm-md"
      onConfirm={canAddToStocktake ? () => addToStocktake() : null}
      onCancel={() => closeModal()}
      confirmText="Add to Stocktake"
      cancelText={canAddToStocktake ? 'Cancel' : 'Close'}
      isSaving={form.isSaving}
      isDisabled={errors.preventSave}
      error={form.serverError}
    >
      {canAddToStocktake && form.referenceID ? (
        <>
          {form.productListQueryParams ? (
            <PluralBlock count={totalToBeAdded}>
              <div
                className={className`${!showKitNotice && 'divider--bottom margin-bottom-20'}`}
              >
                <p className="fs-01 lh-md margin-bottom-0">
                  <strong>
                    Let’s add all filtered products (<Count />) to an existing
                    stocktake.
                  </strong>
                </p>
              </div>
            </PluralBlock>
          ) : (
            <div
              className={className`${!showKitNotice && 'divider--bottom margin-bottom-30'}`}
            >
              <PluralBlock count={totalToBeAdded}>
                <p className="fs-01 lh-md margin-bottom-15">
                  <strong>
                    <IfPlural>
                      <Count /> selected products
                    </IfPlural>
                    <IfSingle>The selected product</IfSingle> can be added to
                    the stocktake.
                  </strong>
                </p>
              </PluralBlock>
            </div>
          )}
          {showKitNotice && (
            <div className="divider--bottom padding-bottom-20 margin-bottom-15">
              {totalToBeAdded > 1 && (
                <PluralBlock count={form.countOfKitSKUs}>
                  <IfPlural>
                    <p className="fs-01 lh-md margin-top-5 margin-bottom-0">
                      <Count /> of the SKUs to be added are{' '}
                      <Plural word="kit" />.
                    </p>
                  </IfPlural>
                  <IfSingle>
                    <p className="fs-01 lh-md margin-top-5 margin-bottom-0">
                      One of the SKUs to be added is a kit.
                    </p>
                  </IfSingle>
                </PluralBlock>
              )}
              {totalToBeAdded === 1 && (
                <p className="fs-01 lh-md margin-top-5 margin-bottom-0">
                  The SKU you’d like to add is a kit.
                </p>
              )}
              <p className="fs-00 lh-md margin-top-15 margin-bottom-0">
                <em>
                  {form.hasKitFilter ? (
                    <span>
                      Including kits in a stocktake could cause inventory
                      discrepancies.
                    </span>
                  ) : (
                    <span>
                      To prevent kits from being added to the stocktake, enable
                      the <strong>Not Kits</strong> filter in Product Filters.
                    </span>
                  )}{' '}
                  <a
                    className="btn btn--link v-align-base darkest border-underline"
                    href="https://support.ordoro.com/how-to-use-the-stocktake-feature-in-ordoro/#kits-in-stocktake"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <strong>Learn more</strong>
                  </a>{' '}
                  →
                </em>
              </p>
            </div>
          )}
        </>
      ) : form.referenceID ? (
        <p className="fs-00 alert alert--warning margin-bottom-10">
          <Icon
            name="exclamation-triangle"
            className="text--warning-orange fs-01 lh-sm v-align-middle margin-right-5"
          />
          No products were included in your selection.
        </p>
      ) : null}
      {canAddToStocktake && (
        <>
          <div className="row">
            <div className="medium-9 columns">
              <PluralBlock count={totalToBeAdded}>
                <p className="fs-01 margin-bottom-10">
                  <strong>
                    Which stocktake do you want <Plural p="them" s="it" /> to go
                    in?
                  </strong>
                </p>
                {form.referenceID ? (
                  <>
                    <p className="fs-01 margin-bottom-0">
                      <strong>{form.referenceID}</strong>
                    </p>
                    <ButtonLink
                      className="fs-00"
                      id="ADD_TO_STOCKTAKE_STOCKTAKE_FILTER__input"
                      onClick={() => updateModalForm({referenceID: null})}
                    >
                      Select a different stocktake
                    </ButtonLink>
                  </>
                ) : (
                  <>
                    <StocktakeFilter
                      dropdown="ADD_TO_STOCKTAKE_STOCKTAKE_FILTER"
                      onSelect={(stocktake) => {
                        updateModalForm({referenceID: stocktake.reference_id})
                      }}
                      autoFocus
                    />
                  </>
                )}
              </PluralBlock>
            </div>
          </div>
          <div className="row margin-top-15">
            <div className="medium-9 columns">
              <Checkbox
                id="get_expected_qty"
                label="Record All Expected Physical On Hand Quantities Now"
                checked={form.autoStart}
                onChange={(autoStart) => updateModalForm({autoStart})}
              />
              <p className="fs-n0 lh-md op-75">
                <em>
                  This option will lock in all CURRENT physical on hand
                  quantities for the SKUs you’re adding to this stocktake. This
                  will not change expected quantities for SKUs already on this
                  stocktake.
                </em>
              </p>
            </div>
          </div>
        </>
      )}
    </ConfirmModal>
  )
}

export default onlyIfForm(AddToStocktakeModal, modalFormSelector)
